/**
 * `/__handoff/:pid` and `/t/:tenant/__handoff/:pid` — Papan's own picker for an
 * app's `papan.files.pickAndRead` and `pickAndSave` (#605).
 *
 * Always THIS page, shown two ways:
 *   - in Papan, as a same-origin iframe in the app window's modal (the host
 *     began the request and waits for `papan:handoff:finished`);
 *   - on pd.app (and share-view), as a popup on this origin (the app began the
 *     request — the server gave it its id — and collects the outcome from the
 *     server itself; on pd.app the popup has no opener to tell: pd.app pages
 *     send COOP `same-origin`).
 * `_headers` lets only this origin frame it, so an app can never draw it,
 * overlay it or read it.
 *
 * The page runs as the signed-in person in the tenant its URL names, so
 * choosing another drive is a navigation — every drive query the picker makes
 * follows the URL's tenant without being told.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { getThemeClass, useTheme } from '@papan/core-ui/ui'
import { handoffApi } from '@papan/files/client/handoff'
import { HandoffPicker, HandoffSavePicker, saveTitle, titleKey } from '@papan/files/ui/handoff'
import { orgsApi, isIdentityOnlyTenant, type TenantInfo } from '@papan/orgs/client'
import { HANDOFF_FINISHED_MESSAGE, type HandoffPageStatus, type HandoffRequestView } from '@papan/types'
import { loginPathWithNext } from '../lib/session-loss'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; view: HandoffRequestView }
  | { kind: 'gone' }
  /** The server could not be reached (the network, an outage) — not "gone":
   *  the request may well be open. The person may try again. */
  | { kind: 'unreachable' }
  /** The app's visitor is signed in as another account than this window. */
  | { kind: 'otherAccount' }
  | { kind: 'done' }

/** How long the page waits for a request that is not there (yet). A popup
 *  opens before its request is registered — usually well under a second. A
 *  begin that fails (too large, a stale session) never registers, and on
 *  pd.app the app cannot close the popup (COOP): the person reads "gone"
 *  after this. Both popup hosts give their begin exactly this long (their
 *  `BEGIN_TIMEOUT_MS`), so a request is never registered behind a page that
 *  already said it is gone. */
const DESCRIBE_PATIENCE_MS = 10_000

const LAST_TENANT_KEY = 'papan.handoff.tenant'
const lastFolderKey = (tenantId: string) => `papan.handoff.folder.${tenantId}`

function readStorage(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeStorage(key: string, value: string): void {
  try { localStorage.setItem(key, value) } catch { /* private window — remembering is a convenience */ }
}

export function HandoffPickerPage() {
  const { t } = useTranslation('files')
  const { pid = '' } = useParams<{ pid: string }>()
  const { isAuthenticated, isLoading } = useAuth()
  const { tenantId, tenantSlug } = useTenant()
  const { settings } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const embedded = typeof window !== 'undefined' && window.parent !== window

  const [state, setState] = useState<PageState>({ kind: 'loading' })
  /** The person's Try again on an unreachable request. */
  const [describeAgain, setDescribeAgain] = useState(0)
  const [tenants, setTenants] = useState<TenantInfo[] | null>(null)
  const settledRef = useRef(false)

  // Signed out → sign in and come straight back (the pid is in the path).
  // `next` is the one the login page reads — through every sign-in, the
  // provider round trip included.
  useEffect(() => {
    if (isLoading || isAuthenticated) return
    navigate(loginPathWithNext(location.pathname), { replace: true })
  }, [isLoading, isAuthenticated, location.pathname, navigate])

  useEffect(() => {
    if (!isAuthenticated || !pid) return
    let cancelled = false
    // A popup opens inside the app's click, BEFORE the app registers the
    // request — so "not found" is at first only "not yet". Keep asking for as
    // long as registering may take (a network blip reads the same) before
    // calling it gone.
    // Past that, what the LAST failure was decides: "not found" is gone; the
    // network or a server hiccup is "couldn't connect" (it may be open).
    const describe = async (): Promise<PageState> => {
      const giveUpAt = Date.now() + DESCRIBE_PATIENCE_MS
      for (;;) {
        try {
          const view = await handoffApi.describe(pid)
          return view.status === 'pending' ? { kind: 'ready', view } : { kind: 'gone' }
        } catch (err) {
          if ((err as { code?: string })?.code === 'HANDOFF_OTHER_ACCOUNT') return { kind: 'otherAccount' }
          if (cancelled || Date.now() >= giveUpAt) {
            const status = (err as { status?: number })?.status
            const unreachable = !status || status === 408 || status === 429 || status >= 500
            return { kind: unreachable ? 'unreachable' : 'gone' }
          }
          await new Promise((r) => setTimeout(r, 500))
        }
      }
    }
    setState({ kind: 'loading' })
    void describe().then((next) => { if (!cancelled) setState(next) })
    return () => { cancelled = true }
  }, [isAuthenticated, pid, describeAgain])

  // The drives this person can choose from — an identity-only tenant has none.
  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // A failed list is asked again, backing off: meanwhile the page goes on in
    // the drive it stands in (no switcher), and the list lands when it can —
    // never "no drives" for the page's whole life after one blip.
    const load = (attempt: number) => orgsApi.listMyTenants().then(
      ({ tenants: all }) => { if (!cancelled) setTenants(all.filter((tn) => !isIdentityOnlyTenant(tn))) },
      () => {
        if (cancelled) return
        setTenants((prev) => prev ?? [])
        timer = setTimeout(() => { void load(attempt + 1) }, Math.min(2_000 * 2 ** attempt, 30_000))
      },
    )
    void load(0)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [isAuthenticated])

  // The drive to browse. The URL's, when this person can browse it — in Papan
  // the modal names the one the person is working in. Opened without one (a
  // popup — pd.app and share-view name none) it is the drive the APP runs in,
  // personal or an organization's, when they belong to it (the server says so
  // only then). Failing that — the drive here is not one they can browse (a
  // personal drive may be identity-only), or none was named: the one they last
  // chose here if they still can, else this one if browsable, else their first.
  const homeTenantId = state.kind === 'ready' ? state.view.homeTenantId ?? null : null
  // Once the picker has been shown, the drive is the person's: a drive list
  // landing late (after a blip) feeds the switcher only — never moves a picker
  // they are already choosing in, taking their ticks with it.
  const pickerShownRef = useRef(false)
  const target = useMemo(() => {
    if (pickerShownRef.current) return tenantId
    const usable = (id: string | null) => !!id && !!tenants?.some((tn) => tn.id === id)
    if (!tenants?.length || (tenantSlug && usable(tenantId))) return tenantId
    if (usable(homeTenantId)) return homeTenantId!
    const last = readStorage(LAST_TENANT_KEY)
    return last && usable(last) ? last : usable(tenantId) ? tenantId : tenants[0]!.id
  }, [tenants, tenantSlug, tenantId, homeTenantId])
  // Only once the request is read: before, the app's drive is not known yet,
  // and a drive chosen then would be the one the page stays in.
  const described = state.kind !== 'loading'
  useEffect(() => {
    if (described && target !== tenantId) navigate(`/t/${encodeURIComponent(target)}/__handoff/${encodeURIComponent(pid)}`, { replace: true })
  }, [described, target, tenantId, pid, navigate])
  // The picker waits for that: never shown in a drive it is about to leave.
  const driveSettled = tenants !== null && target === tenantId
  useEffect(() => {
    if (driveSettled && state.kind === 'ready') pickerShownRef.current = true
  }, [driveSettled, state.kind])

  const isSave = state.kind === 'ready' && state.view.kind === 'save'
  const finish = useCallback((status: HandoffPageStatus) => {
    settledRef.current = true
    if (embedded) {
      // Same origin by construction (`_headers`: frame-ancestors 'self').
      window.parent.postMessage({ type: HANDOFF_FINISHED_MESSAGE, pid, status }, window.location.origin)
      return
    }
    // A save's popup stays after the answer, following the upload to its end —
    // except where the popup is a whole screen of its own (a phone or tablet
    // with no mouse or trackpad): there it hides the app's tab, which is the
    // one sending the file, and a hidden tab is paused (iOS). Close it and
    // the app comes back to the front to send.
    if (status === 'answered' && isSave && !popupHidesApp()) return
    // A popup the app opened closes itself; one opened by hand says it's done.
    window.close()
    setState({ kind: 'done' })
  }, [embedded, pid, isSave])

  // A popup closed with the request still open is the person saying no —
  // including one still loading, one that gave up waiting for the request to
  // appear ("gone"), or one opened in the wrong account: closing still answers
  // it, so the app is not left waiting out the request's lifetime.
  useEffect(() => {
    if (embedded || !isAuthenticated || state.kind === 'done') return
    const onHide = () => { if (!settledRef.current) void handoffApi.cancel(pid) }
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  }, [embedded, isAuthenticated, state.kind, pid])

  useEffect(() => {
    if (state.kind !== 'ready') return
    const { view } = state
    document.title = view.kind === 'save'
      ? saveTitle(view, t)
      : t(`handoff.title.${titleKey(view.policy.select, view.policy.selection.max === 1)}`, { app: view.appName || t('handoff.anApp') })
  }, [state, t])

  const switcher = useMemo(() => {
    if (!tenants || tenants.length < 2) return null
    const label = (tn: TenantInfo) => (tn.type === 'personal' ? t('handoff.page.personalDrive') : tn.name)
    const current = tenants.find((tn) => tn.id === tenantId)
    // What shows is our own box, the same size everywhere. The native select
    // lies over it, unseen, so a tap still opens the platform's own list — and
    // it keeps the 16px a phone needs (index.css: less and iOS zooms in on
    // focus) without that size showing.
    return (
      <label className="relative flex items-center shrink-0 min-w-0 rounded focus-within:ring-2 focus-within:ring-brand-500/60">
        <span className="sr-only">{t('handoff.page.switchDrive')}</span>
        <span aria-hidden="true" className="block max-w-[200px] max-md:max-w-[10rem] truncate text-xs themed-bg-tertiary themed-text rounded pl-2.5 pr-7 py-1 border themed-border">
          {current ? label(current) : ''}
        </span>
        <ChevronDown size={12} aria-hidden="true" className="pointer-events-none absolute right-2 themed-text-muted" />
        <select
          value={tenantId}
          onChange={(e) => {
            writeStorage(LAST_TENANT_KEY, e.target.value)
            navigate(`/t/${encodeURIComponent(e.target.value)}/__handoff/${encodeURIComponent(pid)}`)
          }}
          // Its own list opens at its font size: 12px with a mouse or trackpad
          // at desktop width, matching the box; a phone — and a touch tablet,
          // whose list is the platform's own — keeps 16px (no zoom on focus).
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer md:pointer-fine:text-xs"
        >
          {tenants.map((tn) => (
            <option key={tn.id} value={tn.id}>{label(tn)}</option>
          ))}
        </select>
      </label>
    )
  }, [tenants, tenantId, pid, navigate, t])

  const themeClass = getThemeClass(settings.theme.workspace)
  if (!isAuthenticated || state.kind === 'loading' || (state.kind === 'ready' && !driveSettled)) {
    return <Centered themeClass={themeClass}><p className="themed-text-muted text-sm">{t('handoff.page.loading')}</p></Centered>
  }
  if (state.kind === 'unreachable') {
    return (
      <Centered themeClass={themeClass}>
        <p className="themed-text text-sm font-medium">{t('handoff.page.unreachableTitle')}</p>
        <p className="themed-text-muted text-xs">{t('handoff.page.unreachableBody')}</p>
        <div className="flex justify-center gap-2 mt-2">
          <button type="button" onClick={() => (embedded ? finish('cancelled') : window.close())} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded">
            {t('handoff.page.close')}
          </button>
          <button type="button" onClick={() => setDescribeAgain((n) => n + 1)} className="px-3 py-1.5 text-sm themed-btn-primary rounded">
            {t('handoff.tryAgain')}
          </button>
        </div>
      </Centered>
    )
  }
  if (state.kind === 'gone' || state.kind === 'otherAccount' || state.kind === 'done') {
    const [title, body] = state.kind === 'gone'
      ? [t('handoff.page.goneTitle'), t('handoff.gone')]
      : state.kind === 'otherAccount'
        ? [t('handoff.page.otherAccountTitle'), t('handoff.page.otherAccountBody')]
        : [t('handoff.page.doneTitle'), t('handoff.page.doneBody')]
    return (
      <Centered themeClass={themeClass}>
        <p className="themed-text text-sm font-medium">{title}</p>
        <p className="themed-text-muted text-xs">{body}</p>
        {/* A way out everywhere: a popup closes itself; in Papan's dialog — full
            screen on a phone, no backdrop to tap — the dialog is told to close. */}
        <button
          type="button"
          onClick={() => (embedded ? finish('cancelled') : window.close())}
          className="mt-2 px-3 py-1.5 text-sm themed-btn-primary rounded"
        >
          {t('handoff.page.close')}
        </button>
      </Centered>
    )
  }
  const common = {
    pid,
    tenantId,
    headerAside: switcher,
    initialFolderId: readStorage(lastFolderKey(tenantId)),
    onFolderChange: (folderId: string) => writeStorage(lastFolderKey(tenantId), folderId),
    onFinished: finish,
  }
  return (
    <div className={`${themeClass} h-[100dvh] flex flex-col themed-bg-secondary`}>
      {state.view.kind === 'save'
        // Keyed by the DRIVE, on the element and never through the spread (a
        // `key` inside a spread props object is not a key): switching drives
        // is a new picker. Ticks are ids in ONE tenant and the resolver reads
        // them in one, so ticks carried across kill the whole pick with a
        // bare "unavailable"; folders and places start over with them.
        ? <HandoffSavePicker key={tenantId} {...common} view={state.view} />
        : <HandoffPicker key={tenantId} {...common} view={state.view} />}
    </div>
  )
}

/** A popup on this device takes the whole screen, over the app's tab: touch
 *  only — no mouse or trackpad to have windows side by side with. */
function popupHidesApp(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches
    && !window.matchMedia('(any-pointer: fine)').matches
}

function Centered({ themeClass, children }: { themeClass: string; children: React.ReactNode }) {
  return (
    <div className={`${themeClass} h-[100dvh] flex items-center justify-center themed-bg-secondary`}>
      <div className="text-center space-y-2 px-6">{children}</div>
    </div>
  )
}
